import { motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { content, APP_OGGI } from "@/content/fitswag";
import { StoreButtons } from "@/components/StoreButtons";
import { PhoneFrame } from "@/components/PhoneFrame";

export default function Hero() {
  const { lang } = useLanguage();
  const t = content[lang].hero;
  const reduce = useReducedMotion();
  const scrollDownload = () =>
    document.getElementById("download")?.scrollIntoView({ behavior: "smooth" });

  const rise = (delay = 0) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section id="home" className="relative overflow-hidden bg-black pt-28 md:pt-36">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-brand-orange/20 blur-[150px]" />
        <div className="absolute right-0 top-52 h-[340px] w-[340px] rounded-full bg-brand-amber/10 blur-[120px]" />
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 md:grid-cols-2 md:gap-8 md:px-8 md:pb-28">
        <div>
          <motion.span
            data-testid="hero-badge"
            {...rise(0)}
            className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-brand-orange"
          >
            {t.badge}
          </motion.span>

          <h1 className="mt-6 font-display text-white">
            {t.titleLines.map((line, i) => (
              <motion.span
                key={i}
                {...rise(0.1 + i * 0.1)}
                className="block text-5xl leading-[0.98] tracking-tighter sm:text-6xl md:text-7xl"
              >
                {i === t.titleLines.length - 1 ? (
                  <span className="text-brand-orange">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            ))}
          </h1>

          <motion.p
            {...rise(0.35)}
            className="mt-7 max-w-xl text-lg leading-relaxed text-white/70 md:text-xl"
          >
            {t.subtitle}
          </motion.p>

          <motion.div {...rise(0.45)} className="mt-9">
            <StoreButtons
              primary={t.ctaPrimary}
              secondary={t.ctaSecondary}
              testPrefix="hero"
              onDownload={scrollDownload}
            />
            <p className="mt-4 text-sm text-white/45">{t.note}</p>
          </motion.div>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.92, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <PhoneFrame src={APP_OGGI} alt="FitSwag — Oggi" glow size="md" className="md:ml-auto" />
        </motion.div>
      </div>
    </section>
  );
}
